import type {
  Action,
  BorradorInicial,
  CreateRulePayload,
  PricingRule,
  RoundMode,
  RuleScope,
  TipoDeRegla,
  UpdateRulePayload,
} from '../types/pricing-rules.types';
import {
  describirAlcance,
  describirReglaCompleta,
  esDeUnProducto,
  esLimiteEnBs,
  tipoDeRegla,
  topesDeMovimiento,
} from './reglas-en-palabras';

/**
 * Lo que arma Nueva regla (maqueta S2–S4), sin pantalla: se prueba solo.
 */

export interface EstadoLimite {
  tipo: TipoDeRegla | null;
  /** Margen mínimo, distancia al más barato (arriba o abajo) o, en Bs, el monto. */
  valor: string;
  /** Movimiento (decisión 8): los dos opcionales; vacío = ese sentido sin tope. */
  subida: string;
  bajada: string;
  mode: RoundMode;
  customValue: string;
}

export const LIMITE_VACIO: EstadoLimite = {
  tipo: null,
  valor: '',
  subida: '',
  bajada: '',
  mode: 'NEAREST_99',
  customValue: '',
};

export type FormaDeAlcance = 'CATALOGO' | 'CATEGORIA_MARCA' | 'PUNTUALES';

export interface EstadoAlcance {
  forma: FormaDeAlcance;
  categories: string[];
  brands: string[];
  skus: string[];
  excludedSkus: string[];
}

export const ALCANCE_VACIO: EstadoAlcance = {
  forma: 'CATALOGO',
  categories: [],
  brands: [],
  skus: [],
  excludedSkus: [],
};

/** Un número entre `min` y `max`, o null. Acepta coma decimal («12,5»). */
function numero(v: string, min: number, max: number): number | null {
  if (v.trim() === '') return null;
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) && n >= min && n <= max ? n : null;
}

/**
 * Un monto en Bs como lo escribe una persona en Bolivia (lote B2): «5000»,
 * «5000,50», «5.000» o «5.000,50». Mayor que 0 y con dos decimales como mucho
 * (auditoría H14), o null.
 */
export function montoEnBs(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const conMiles = /^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(t);
  if (!conMiles && !/^\d+([.,]\d{1,2})?$/.test(t)) return null;
  const n = Number(conMiles ? t.replace(/\./g, '').replace(',', '.') : t.replace(',', '.'));
  return Number.isFinite(n) && n > 0 && n <= 1_000_000_000 ? n : null;
}

/**
 * Las acciones de la regla, o `null` si falta algo o un número no sirve. Los
 * topes son los del backend (`pricing-rules.schemas.ts`): margen 0–99,
 * movimiento 0–100, distancia al más barato 0–500 arriba y 0–99 abajo; un
 * monto en Bs, mayor que 0.
 */
export function accionesDe(e: EstadoLimite): Action[] | null {
  switch (e.tipo) {
    case 'MARGEN_MINIMO': {
      const p = numero(e.valor, 0, 99);
      return p == null ? null : [{ type: 'FORCE_MIN_MARGIN', percent: p }];
    }
    case 'SOBRE_EL_MAS_BARATO': {
      const p = numero(e.valor, 0, 500);
      return p == null ? null : [{ type: 'MAX_ABOVE_COMPETITOR_MIN', percent: p }];
    }
    case 'BAJO_EL_MAS_BARATO': {
      const p = numero(e.valor, 0, 99);
      return p == null ? null : [{ type: 'MAX_BELOW_COMPETITOR_MIN', percent: p }];
    }
    case 'PISO_FIJO': {
      const v = montoEnBs(e.valor);
      return v == null ? null : [{ type: 'FORCE_FLOOR', value: v }];
    }
    case 'TECHO_FIJO': {
      const v = montoEnBs(e.valor);
      return v == null ? null : [{ type: 'FORCE_MAX_PRICE', value: v }];
    }
    case 'MOVIMIENTO': {
      const vacioS = e.subida.trim() === '';
      const vacioB = e.bajada.trim() === '';
      const s = numero(e.subida, 0, 100);
      const b = numero(e.bajada, 0, 100);
      if ((!vacioS && s == null) || (!vacioB && b == null)) return null;
      if (s == null && b == null) return null;
      if (s != null && b != null) {
        return s === b
          ? [{ type: 'MAX_CHANGE_PERCENT', percent: s }]
          : [
              { type: 'MAX_CHANGE_PERCENT', percent: s, direction: 'UP' },
              { type: 'MAX_CHANGE_PERCENT', percent: b, direction: 'DOWN' },
            ];
      }
      return s != null
        ? [{ type: 'MAX_CHANGE_PERCENT', percent: s, direction: 'UP' }]
        : [{ type: 'MAX_CHANGE_PERCENT', percent: b!, direction: 'DOWN' }];
    }
    case 'REDONDEO': {
      if (e.mode !== 'CUSTOM') return [{ type: 'ROUND_PRICE', mode: e.mode }];
      const c = numero(e.customValue, 0.01, 1_000_000);
      return c == null ? null : [{ type: 'ROUND_PRICE', mode: 'CUSTOM', customValue: c }];
    }
    default:
      return null;
  }
}

/** Para editar: lo que la regla tiene, en los casilleros. Una regla vieja deja el tipo sin elegir. */
export function limiteDesde(actions: Action[]): EstadoLimite {
  const tipo = tipoDeRegla(actions);
  if (tipo == null) return { ...LIMITE_VACIO };
  const a = actions[0];
  if (tipo === 'MOVIMIENTO') {
    const t = topesDeMovimiento(actions)!;
    return {
      ...LIMITE_VACIO,
      tipo,
      subida: t.subida == null ? '' : String(t.subida),
      bajada: t.bajada == null ? '' : String(t.bajada),
    };
  }
  if (a.type === 'ROUND_PRICE') {
    return {
      ...LIMITE_VACIO,
      tipo,
      mode: a.mode,
      customValue: a.customValue == null ? '' : String(a.customValue),
    };
  }
  return {
    ...LIMITE_VACIO,
    tipo,
    // Ronda final B2: un monto guardado con decimales largos (7279.999999999999)
    // no lo lee `montoEnBs`; se abre redondeado a centavos.
    valor:
      'percent' in a
        ? String(a.percent)
        : 'value' in a
          ? String(Math.round(a.value * 100) / 100)
          : '',
  };
}

export function limiteDesdeInicial(b: BorradorInicial): EstadoLimite {
  if (b.tipo === 'MOVIMIENTO')
    return { ...LIMITE_VACIO, tipo: b.tipo, subida: String(b.valor), bajada: String(b.valor) };
  return { ...LIMITE_VACIO, tipo: b.tipo, valor: String(b.valor) };
}

/** Las tres formas son excluyentes (maqueta S3): productos puntuales no se mezcla con lo demás. */
export function scopeDe(a: EstadoAlcance): RuleScope {
  const s: RuleScope = {};
  if (a.forma === 'PUNTUALES') {
    if (a.skus.length) s.skus = [...a.skus];
    return s;
  }
  if (a.forma === 'CATEGORIA_MARCA') {
    if (a.categories.length) s.categories = [...a.categories];
    if (a.brands.length) s.brands = [...a.brands];
  }
  if (a.excludedSkus.length) s.excludedSkus = [...a.excludedSkus];
  return s;
}

export function alcanceDesde(s: RuleScope | null | undefined): EstadoAlcance {
  const base = {
    ...ALCANCE_VACIO,
    categories: s?.categories ?? [],
    brands: s?.brands ?? [],
    skus: s?.skus ?? [],
    excludedSkus: s?.excludedSkus ?? [],
  };
  if (base.skus.length) return { ...base, forma: 'PUNTUALES' };
  if (base.categories.length || base.brands.length) return { ...base, forma: 'CATEGORIA_MARCA' };
  return { ...base, forma: 'CATALOGO' };
}

/**
 * Productos puntuales sin ningún producto no se puede contar ni guardar: sería
 * todo el catálogo. Lote B2 (D6): un límite en Bs, exactamente un producto.
 */
export const alcanceListo = (a: EstadoAlcance, tipo: TipoDeRegla | null = null) =>
  esLimiteEnBs(tipo)
    ? a.forma === 'PUNTUALES' && a.skus.length === 1
    : a.forma !== 'PUNTUALES' || a.skus.length > 0;

/** «No ganar menos de 20 % · Sony». El backend acepta hasta 200 letras. */
export function nombreAutomatico(actions: Action[], s: RuleScope): string {
  const alcance = describirAlcance(s);
  const n =
    alcance === 'Todo el catálogo'
      ? describirReglaCompleta(actions)
      : `${describirReglaCompleta(actions)} · ${alcance}`;
  return n.length > 200 ? `${n.slice(0, 199)}…` : n;
}

/**
 * El nombre que se guarda (Sebastián, 27-sep): el que escribió, o el automático si
 * lo dejó vacío. La lista muestra siempre el límite al lado, así un nombre propio
 * no puede esconder lo que la regla hace.
 */
export function nombreDeRegla(nombre: string, actions: Action[], s: RuleScope): string {
  const propio = nombre.trim();
  if (!propio) return nombreAutomatico(actions, s);
  return propio.length > 200 ? propio.slice(0, 200) : propio;
}

/** El nombre de una regla es propio si no es el que se arma solo con su límite y alcance. */
export function tieneNombrePropio(r: PricingRule): boolean {
  return r.name !== nombreAutomatico(r.actions, r.scope ?? {});
}

/**
 * Lo que trae el campo «Nombre» al abrir una regla: su nombre propio, o vacío si
 * era el automático, para que siga a lo que se cambie del límite o del alcance.
 * Una regla de la pantalla anterior (límite que ya no existe) también abre vacío:
 * al guardarla le queda otro límite y su nombre viejo lo describiría mal.
 */
export function nombreInicial(r: PricingRule | null): string {
  return r && tipoDeRegla(r.actions) != null && tieneNombrePropio(r) ? r.name : '';
}

/** La frase en vivo de S3: «Alcanza a todos los productos Sony, de cualquier categoría». */
export function fraseDeAlcance(a: EstadoAlcance, tipo: TipoDeRegla | null = null): string {
  const menos = a.excludedSkus.length
    ? `, menos ${a.excludedSkus.length === 1 ? '1 excepción' : `${a.excludedSkus.length} excepciones`}`
    : '';
  // Lote B2 (D6): un límite en Bs es de un solo producto.
  if (esLimiteEnBs(tipo)) {
    if (a.skus.length === 0) return 'Elegí el producto.';
    if (a.skus.length > 1) return 'Un límite en Bs es sólo para un producto: dejá uno solo.';
    return 'Alcanza sólo a este producto.';
  }
  if (a.forma === 'PUNTUALES') {
    return a.skus.length === 0
      ? 'Elegí al menos un producto.'
      : `Alcanza sólo a ${a.skus.length === 1 ? 'este producto' : `estos ${a.skus.length} productos`}.`;
  }
  if (a.forma === 'CATEGORIA_MARCA' && (a.categories.length || a.brands.length)) {
    const cats = a.categories.join(', ');
    const marcas = a.brands.join(', ');
    if (a.categories.length && a.brands.length)
      return `Alcanza sólo a los productos ${marcas} de ${cats}: tienen que cumplirse las dos${menos}.`;
    if (a.categories.length)
      return `Alcanza a todos los productos de ${cats}, de cualquier marca${menos}.`;
    return `Alcanza a todos los productos ${marcas}, de cualquier categoría${menos}.`;
  }
  return `Alcanza a todo el catálogo${menos}.`;
}

/**
 * El cuerpo del alta o de la edición. Sin prioridad, sin grupo, sin vigencia:
 * el backend pone lo suyo. Las condiciones van vacías siempre: así una regla
 * vieja con condiciones deja de tenerlas al editarla (el PATCH sin
 * `conditions` las conservaría y la regla seguiría sin correr). Al EDITAR no se
 * manda `isActive`: guardar no prende una regla apagada.
 */
export function cuerpoDeGuardado(args: {
  actions: Action[];
  scope: RuleScope;
  editando: PricingRule | null;
  encender: boolean;
  /** Lo escrito en «Nombre»; vacío o ausente = el automático. */
  nombre?: string;
}): CreateRulePayload | UpdateRulePayload {
  const base = {
    name: nombreDeRegla(args.nombre ?? '', args.actions, args.scope),
    scope: args.scope,
    conditions: { all: [] },
    actions: args.actions,
  };
  return args.editando ? base : { ...base, isActive: args.encender };
}

/**
 * Lo que cambia de una regla vieja al guardarla con la pantalla nueva. `null` = nada.
 *
 * `borrador` es lo que la persona tiene elegido ahora (sin él, se supone que
 * guarda lo que la regla ya tenía): con él no se avisa de la mezcla de
 * productos puntuales si eligió otra forma.
 */
export function avisoAlEditar(
  r: PricingRule | null,
  borrador?: { acciones: Action[] | null; alcance: EstadoAlcance }
): string | null {
  if (!r) return null;
  const partes: string[] = [];
  // Productos puntuales no se mezcla (las tres formas son excluyentes):
  // `alcanceDesde` la abre en PUNTUALES y `scopeDe` deja fuera lo demás.
  const s = r.scope ?? {};
  const mezclaba =
    (s.skus?.length ?? 0) > 0 &&
    ((s.categories?.length ?? 0) > 0 ||
      (s.brands?.length ?? 0) > 0 ||
      (s.excludedSkus?.length ?? 0) > 0);
  if (mezclaba && (!borrador || borrador.alcance.forma === 'PUNTUALES'))
    partes.push(
      'Tenía productos puntuales junto con otras formas de elegir (categorías, marcas o excepciones): queda sólo con los productos puntuales.'
    );
  if ((r.conditions?.all?.length ?? 0) > 0)
    partes.push(
      'Tenía requisitos extra que ya no existen: al guardarla va a alcanzar a todos los productos de su alcance (abajo ves a cuántos).'
    );
  // Lote B2 (D6): una regla vieja con un monto fijo para varios productos.
  if (esLimiteEnBs(tipoDeRegla(r.actions)) && !esDeUnProducto(r.scope))
    partes.push(
      'Pone un límite en Bs a varios productos: ahora es sólo para uno. Al guardarla, elegí un solo producto.'
    );
  if (tipoDeRegla(r.actions) == null)
    partes.push(
      `Se creó con la pantalla anterior («${describirReglaCompleta(r.actions)}»): al guardarla queda sólo el límite que elegiste acá.`
    );
  // El nombre ya no se avisa: se ve y se edita en el campo «Nombre» (27-sep).
  return partes.length ? partes.join(' ') : null;
}
